import React from 'react'
import PropTypes from 'prop-types'
import { Controller, useWatch } from 'react-hook-form'
import {
  Checkbox,
  FormControlLabel,
  FormLabel,
  Grid,
  TextField,
  Typography,
} from '@the-platform-group/component-library'
import { Select } from '@the-platform-group/component-library/Select'

import { AGENTIC_PROMPT_CATEGORIES, AGENTIC_PROMPT_STATUSES } from 'constants/agenticPrompts'

import ParentPromptSelect from './ParentPromptSelect'

const categoryOptions = AGENTIC_PROMPT_CATEGORIES.map(category => ({
  value: category,
  label: category,
}))

const statusOptions = AGENTIC_PROMPT_STATUSES.map(status => ({
  value: status,
  label: status,
}))

/**
 * Renders the editable fields for an agentic prompt. Conditionally shows the
 * parent prompt picker when isChild is checked.
 * @param {object} props - component props
 * @param {object} props.control - react-hook-form control object
 * @param {object} props.formState - react-hook-form formState object
 * @param {string} [props.promptId] - id of the prompt being edited (used to exclude from parent dropdown)
 * @returns {React.ReactElement} the form fields
 */
const PromptFormFields = ({ control, formState, promptId }) => {
  const { errors } = formState
  const isChild = useWatch({ control, name: 'isChild' })

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <FormLabel htmlFor="title">Title</FormLabel>
        <Controller
          name="title"
          control={control}
          rules={{ required: 'Title is required' }}
          render={({ field }) => (
            <TextField
              {...field}
              id="title"
              fullWidth
              variant="outlined"
              {...(errors.title && { error: true })}
              helperText={errors.title?.message}
              sx={{ mt: 1 }}
            />
          )}
        />
      </Grid>

      <Grid item xs={12}>
        <FormLabel htmlFor="body">Prompt body</FormLabel>
        <Controller
          name="body"
          control={control}
          rules={{ required: 'Prompt body is required' }}
          render={({ field }) => (
            <TextField
              {...field}
              id="body"
              fullWidth
              multiline
              minRows={3}
              variant="outlined"
              {...(errors.body && { error: true })}
              helperText={errors.body?.message}
              sx={{ mt: 1 }}
            />
          )}
        />
      </Grid>

      <Grid item xs={12}>
        <FormLabel htmlFor="categories">Categories</FormLabel>
        <Controller
          name="categories"
          control={control}
          rules={{
            validate: value => (value && value.length > 0) || 'Please select at least one category',
          }}
          render={({ field }) => (
            <Select
              id="categories"
              fullWidth
              isMulti
              options={categoryOptions}
              value={field.value || []}
              onChange={event => field.onChange(event.target.value)}
              hasError={!!errors.categories}
              helperText={errors.categories?.message}
              sx={{ mt: 1 }}
            />
          )}
        />
      </Grid>

      <Grid item xs={12}>
        <FormLabel htmlFor="status">Status</FormLabel>
        <Typography variant="body2" color="textSecondary">
          Controls whether this prompt is available to users in the live application.
        </Typography>
        <Controller
          name="status"
          control={control}
          render={({ field }) => (
            <Select
              id="status"
              fullWidth
              options={statusOptions}
              value={field.value || 'draft'}
              onChange={event => field.onChange(event.target.value)}
              sx={{ mt: 1 }}
            />
          )}
        />
      </Grid>

      <Grid item xs={12}>
        <Typography variant="subtitle2">Hierarchy</Typography>
        <Controller
          name="isChild"
          control={control}
          render={({ field: { value, onChange, ...field } }) => (
            <FormControlLabel
              label="This is a child prompt"
              control={
                <Checkbox
                  {...field}
                  checked={!!value}
                  onChange={event => onChange(event.target.checked)}
                />
              }
            />
          )}
        />
      </Grid>

      {isChild && (
        <Grid item xs={12}>
          <Controller
            name="parentPrompt"
            control={control}
            shouldUnregister
            rules={{
              validate: value => !!value?.id || 'Parent prompt is required for child prompts',
            }}
            render={({ field: { value, onChange } }) => (
              <ParentPromptSelect
                value={value}
                onChange={onChange}
                excludeId={promptId}
                error={!!errors.parentPrompt}
                helperText={errors.parentPrompt?.message}
              />
            )}
          />
        </Grid>
      )}
    </Grid>
  )
}

PromptFormFields.propTypes = {
  control: PropTypes.object.isRequired,
  formState: PropTypes.object.isRequired,
  promptId: PropTypes.string,
}

export default PromptFormFields
